/**
 * useOverlayPosition - Hook for managing draggable overlay position.
 *
 * Provides state and event handlers for implementing draggable floating
 * overlays over the globe canvas.
 */

import { useState, useCallback, useRef, useEffect } from 'react'

export interface OverlayPosition {
  x: number
  y: number
}

export interface UseOverlayPositionReturn {
  /** Current position of the overlay */
  position: OverlayPosition
  /** Whether the overlay is currently being dragged */
  isDragging: boolean
  /** Event handlers to spread on the drag handle element */
  handlers: {
    onMouseDown: (e: React.MouseEvent) => void
    onTouchStart: (e: React.TouchEvent) => void
  }
  /** Programmatically set the position */
  setPosition: (position: OverlayPosition) => void
  /** Reset position to initial value */
  resetPosition: () => void
}

/**
 * Hook for managing draggable overlay position.
 *
 * @param initialPosition - Initial x,y position of the overlay
 * @param bounds - Optional bounds to constrain the overlay within
 *
 * @example
 * ```tsx
 * function MyOverlay() {
 *   const { position, handlers } = useOverlayPosition({ x: 20, y: 20 })
 *
 *   return (
 *     <div style={{ left: position.x, top: position.y }}>
 *       <div {...handlers}>Drag handle</div>
 *       <div>Content</div>
 *     </div>
 *   )
 * }
 * ```
 */
export function useOverlayPosition(
  initialPosition: OverlayPosition = { x: 20, y: 20 },
  bounds?: { minX?: number; maxX?: number; minY?: number; maxY?: number }
): UseOverlayPositionReturn {
  const [position, setPositionState] = useState<OverlayPosition>(initialPosition)
  const [isDragging, setIsDragging] = useState(false)

  // Track drag start point and initial position
  const dragStartRef = useRef<{ mouseX: number; mouseY: number; posX: number; posY: number } | null>(null)

  /**
   * Constrain position within bounds if specified.
   */
  const constrainPosition = useCallback(
    (pos: OverlayPosition): OverlayPosition => {
      if (!bounds) return pos

      return {
        x: Math.max(
          bounds.minX ?? 0,
          Math.min(bounds.maxX ?? window.innerWidth, pos.x)
        ),
        y: Math.max(
          bounds.minY ?? 0,
          Math.min(bounds.maxY ?? window.innerHeight, pos.y)
        ),
      }
    },
    [bounds]
  )

  /**
   * Start drag operation.
   */
  const startDrag = useCallback(
    (clientX: number, clientY: number) => {
      dragStartRef.current = {
        mouseX: clientX,
        mouseY: clientY,
        posX: position.x,
        posY: position.y,
      }
      setIsDragging(true)
    },
    [position.x, position.y]
  )

  /**
   * Update position during drag.
   */
  const updateDrag = useCallback(
    (clientX: number, clientY: number) => {
      if (!dragStartRef.current) return

      const deltaX = clientX - dragStartRef.current.mouseX
      const deltaY = clientY - dragStartRef.current.mouseY

      const newPosition = constrainPosition({
        x: dragStartRef.current.posX + deltaX,
        y: dragStartRef.current.posY + deltaY,
      })

      setPositionState(newPosition)
    },
    [constrainPosition]
  )

  /**
   * End drag operation.
   */
  const endDrag = useCallback(() => {
    dragStartRef.current = null
    setIsDragging(false)
  }, [])

  // Mouse event handlers
  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      startDrag(e.clientX, e.clientY)
    },
    [startDrag]
  )

  // Touch event handlers
  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length !== 1) return
      const touch = e.touches[0]
      startDrag(touch.clientX, touch.clientY)
    },
    [startDrag]
  )

  // Global mouse/touch move and up handlers
  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      updateDrag(e.clientX, e.clientY)
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return
      const touch = e.touches[0]
      updateDrag(touch.clientX, touch.clientY)
    }

    const handleMouseUp = () => {
      endDrag()
    }

    const handleTouchEnd = () => {
      endDrag()
    }

    // Add global listeners
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('touchmove', handleTouchMove)
    window.addEventListener('touchend', handleTouchEnd)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [isDragging, updateDrag, endDrag])

  const setPosition = useCallback(
    (newPosition: OverlayPosition) => {
      setPositionState(constrainPosition(newPosition))
    },
    [constrainPosition]
  )

  const resetPosition = useCallback(() => {
    setPositionState(initialPosition)
  }, [initialPosition])

  return {
    position,
    isDragging,
    handlers: {
      onMouseDown,
      onTouchStart,
    },
    setPosition,
    resetPosition,
  }
}
